/**
 * Portfolio Redux Slice
 * Manages portfolio state (consolidated portfolio, holdings)
 * 
 * ⚠️ CRITICAL: Per ADR-FE-001, this slice ONLY stores pre-calculated data from backend.
 * Backend Portfolio Service performs all calculations and aggregations.
 * Frontend displays the data as-is.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ConsolidatedPortfolio, DetailedHolding, PortfolioTransaction, PortfolioSyncStatus, PortfolioSyncHistory } from '../../types/portfolio';
import type { PortfolioSyncRequest } from '../../api/generated/api';
import { PortfolioSyncRequestSyncTypeEnum, PortfolioSyncRequestSyncScopeEnum } from '../../api/generated/api';

export interface PortfolioState {
  consolidated: ConsolidatedPortfolio | null;
  holdings: DetailedHolding[]; // Detailed holdings list
  byBroker: Record<string, DetailedHolding[]>; // Holdings grouped by broker account
  transactions: PortfolioTransaction[]; // Transaction history
  syncStatus: PortfolioSyncStatus[]; // Recent sync status per broker account
  syncHistory: PortfolioSyncHistory[]; // Sync history
  transactionsTotal: number; // Total transaction count for pagination
  transactionsLoading: boolean; // Loading state for transactions
  syncLoading: boolean; // Loading state for sync status
  lastUpdated: number | null;
  loading: boolean;
  error: string | null;
  syncInProgress: Record<string, boolean>; // Track sync status per broker account
}

const initialState: PortfolioState = {
  consolidated: null,
  holdings: [],
  byBroker: {},
  transactions: [],
  syncStatus: [],
  syncHistory: [],
  transactionsTotal: 0,
  transactionsLoading: false,
  syncLoading: false,
  lastUpdated: null,
  loading: false,
  error: null,
  syncInProgress: {},
};

/**
 * Fetch consolidated portfolio from backend
 * ⚠️ IMPORTANT: Per ADR-FE-001, this only fetches pre-calculated data.
 * Backend Portfolio Service performs all calculations and aggregations.
 */
export const fetchConsolidatedPortfolio = createAsyncThunk(
  'portfolio/fetchConsolidated',
  async (_, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.portfolioGet();
      
      // Map backend response to frontend types
      const portfolio: ConsolidatedPortfolio = {
        firebase_uid: response.data.firebase_uid || '',
        total_accounts: response.data.total_accounts || 0,
        total_holdings: response.data.total_holdings || 0,
        portfolio_summary: {
          total_market_value: parseFloat(response.data.portfolio_summary?.total_market_value || '0'),
          total_invested_value: parseFloat(response.data.portfolio_summary?.total_invested_value || '0'),
          total_unrealized_pnl: parseFloat(response.data.portfolio_summary?.total_unrealized_pnl || '0'),
          total_unrealized_pnl_percentage: parseFloat(response.data.portfolio_summary?.total_unrealized_pnl_percentage || '0'),
          total_day_change: parseFloat(response.data.portfolio_summary?.total_day_change || '0'),
          total_day_change_percentage: parseFloat(response.data.portfolio_summary?.total_day_change_percentage || '0'),
        },
        broker_accounts: (response.data.broker_accounts || []).map((acc: any) => ({
          broker_account_id: acc.broker_account_id || '',
          broker_name: acc.broker_name || '',
          broker_type: acc.broker_type || '',
          market_value: parseFloat(acc.market_value || '0'),
          invested_value: parseFloat(acc.invested_value || '0'),
          unrealized_pnl: parseFloat(acc.unrealized_pnl || '0'),
          unrealized_pnl_percentage: parseFloat(acc.unrealized_pnl_percentage || '0'),
          day_change: parseFloat(acc.day_change || '0'),
          day_change_percentage: parseFloat(acc.day_change_percentage || '0'),
          holdings_count: acc.holdings_count || 0,
        })),
        top_holdings: (response.data.top_holdings || []).map((holding: any) => ({
          id: holding.id || holding.instrument_id || '',
          instrument_id: holding.instrument_id || '',
          symbol: holding.symbol || '',
          exchange: holding.exchange,
          total_quantity: parseFloat(holding.total_quantity || holding.quantity || '0'),
          weighted_average_price: parseFloat(holding.weighted_average_price || holding.average_price || '0'),
          current_price: parseFloat(holding.current_price || holding.market_value || '0'),
          total_invested_value: parseFloat(holding.total_invested_value || holding.invested_value || '0'),
          total_market_value: parseFloat(holding.total_market_value || holding.market_value || '0'),
          total_unrealized_pnl: parseFloat(holding.total_unrealized_pnl || holding.unrealized_pnl || '0'),
          total_unrealized_pnl_percentage: parseFloat(holding.total_unrealized_pnl_percentage || holding.unrealized_pnl_percentage || '0'),
          day_change: parseFloat(holding.day_change || '0'),
          day_change_percentage: parseFloat(holding.day_change_percentage || holding.day_change_percentage || '0'),
          broker_holdings: holding.broker_holdings || [],
        })),
        last_updated: response.data.last_updated || new Date().toISOString(),
      };
      
      return portfolio;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch portfolio';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Fetch detailed holdings list from backend
 * ⚠️ IMPORTANT: Per ADR-FE-001, this only fetches pre-calculated data.
 */
export const fetchHoldings = createAsyncThunk(
  'portfolio/fetchHoldings',
  async (params: { brokerAccountId?: string; symbol?: string; exchange?: string } = {}, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.portfolioHoldingsList(
        params.brokerAccountId,
        params.symbol,
        params.exchange
      );
      
      // Map backend response to frontend types
      const holdings: DetailedHolding[] = (response.data.holdings || []).map((holding: any) => ({
        id: holding.id || '',
        firebase_uid: holding.firebase_uid || '',
        broker_account_id: holding.broker_account_id || '',
        instrument_id: holding.instrument_id || '',
        broker_type: holding.broker_type,
        symbol: holding.symbol,
        exchange: holding.exchange,
        quantity: parseFloat(holding.quantity || '0'),
        available_quantity: holding.available_quantity ? parseFloat(holding.available_quantity) : undefined,
        average_price: holding.average_price ? parseFloat(holding.average_price) : undefined,
        last_price: holding.last_price ? parseFloat(holding.last_price) : undefined,
        close_price: holding.close_price ? parseFloat(holding.close_price) : undefined,
        invested_value: holding.invested_value ? parseFloat(holding.invested_value) : undefined,
        market_value: parseFloat(holding.market_value || '0'),
        unrealized_pnl: holding.unrealized_pnl ? parseFloat(holding.unrealized_pnl) : undefined,
        unrealized_pnl_percentage: holding.unrealized_pnl_percentage ? parseFloat(holding.unrealized_pnl_percentage) : undefined,
        day_change: holding.day_change ? parseFloat(holding.day_change) : undefined,
        day_change_percentage: holding.day_change_percentage ? parseFloat(holding.day_change_percentage) : undefined,
        net_change: holding.net_change ? parseFloat(holding.net_change) : undefined,
        net_change_percentage: holding.net_change_percentage ? parseFloat(holding.net_change_percentage) : undefined,
        last_synced_at: holding.last_synced_at,
      }));
      
      return holdings;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch holdings';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Trigger portfolio sync for a broker account
 * ⚠️ IMPORTANT: Per ADR-FE-001, this only triggers backend sync.
 * Backend handles all sync logic.
 */
export const syncPortfolio = createAsyncThunk(
  'portfolio/sync',
  async (params: { brokerAccountId?: string; scope?: 'holdings_only' | 'transactions_only' | 'both' }, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');

      // Build body: omit broker_account_id for "sync all", include for single-account sync
      const scopeEnum =
        params.scope === 'holdings_only'
          ? PortfolioSyncRequestSyncScopeEnum.HoldingsOnly
          : params.scope === 'transactions_only'
            ? PortfolioSyncRequestSyncScopeEnum.TransactionsOnly
            : PortfolioSyncRequestSyncScopeEnum.Both;
      const body: PortfolioSyncRequest = {
        sync_type: PortfolioSyncRequestSyncTypeEnum.Full,
        sync_scope: scopeEnum,
      };
      if (params.brokerAccountId) {
        body.broker_account_id = params.brokerAccountId;
      }

      const response = await api.portfolioSync(body);

      const data = response.data as Record<string, unknown>;
      // Single-account response: sync_id, broker_account_id, sync_status, ...
      if ('sync_id' in data && data.sync_id) {
        return {
          syncId: String(data.sync_id),
          status: (data.sync_status as string) || 'pending',
          brokerAccountId: (data.broker_account_id as string) || params.brokerAccountId,
          accountsSynced: 1,
        };
      }
      // Sync-all response: accounts_synced, results, message
      if ('accounts_synced' in data) {
        return {
          syncId: '',
          status: 'success',
          brokerAccountId: undefined,
          accountsSynced: (data.accounts_synced as number) ?? 0,
        };
      }
      return {
        syncId: '',
        status: 'pending',
        brokerAccountId: params.brokerAccountId,
        accountsSynced: 0,
      };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to sync portfolio';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Fetch transactions from backend
 */
export const fetchTransactions = createAsyncThunk(
  'portfolio/fetchTransactions',
  async (params: {
    brokerAccountId?: string;
    transactionType?: string;
    symbol?: string;
    exchange?: string;
    fromDate?: string;
    toDate?: string;
    limit?: number;
    offset?: number;
  } = {}, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.portfolioTransactionsList(
        params.brokerAccountId,
        params.transactionType,
        params.symbol,
        params.exchange,
        params.fromDate,
        params.toDate,
        params.limit,
        params.offset
      );
      
      const transactions: PortfolioTransaction[] = (response.data.transactions || []).map((tx: any) => ({
        id: tx.id || '',
        firebase_uid: tx.firebase_uid || '',
        broker_account_id: tx.broker_account_id || '',
        instrument_id: tx.instrument_id || '',
        broker_type: tx.broker_type,
        transaction_type: tx.transaction_type || 'buy',
        transaction_date: tx.transaction_date || '',
        trade_date: tx.trade_date,
        quantity: parseFloat(tx.quantity || '0'),
        price: parseFloat(tx.price || '0'),
        transaction_value: tx.transaction_value ? parseFloat(tx.transaction_value) : undefined,
        total_charges: tx.total_charges ? parseFloat(tx.total_charges) : undefined,
        net_amount: tx.net_amount ? parseFloat(tx.net_amount) : undefined,
        symbol: tx.symbol,
        exchange: tx.exchange,
      }));
      
      return {
        transactions,
        total: response.data.total_count || 0,
      };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch transactions';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Fetch sync status from backend
 */
export const fetchSyncStatus = createAsyncThunk(
  'portfolio/fetchSyncStatus',
  async (params: { brokerAccountId: string; limit?: number }, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.portfolioSyncStatus(params.brokerAccountId, params.limit);
      
      const syncStatus: PortfolioSyncStatus[] = (response.data || []).map((sync: any) => ({
        id: sync.id,
        firebase_uid: sync.firebase_uid,
        broker_account_id: params.brokerAccountId,
        broker_type: sync.broker_type,
        sync_type: sync.sync_type,
        sync_trigger: sync.sync_trigger,
        sync_status: sync.sync_status || 'pending',
        sync_started_at: sync.sync_started_at,
        sync_completed_at: sync.sync_completed_at,
        sync_duration_ms: sync.sync_duration_ms,
        holdings_synced_count: sync.holdings_synced_count,
        transactions_synced_count: sync.transactions_synced_count,
        error_message: sync.error_message,
        error_code: sync.error_code,
      }));
      
      return syncStatus;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch sync status';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Fetch sync history from backend
 */
export const fetchSyncHistory = createAsyncThunk(
  'portfolio/fetchSyncHistory',
  async (params: { brokerAccountId: string; limit?: number }, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.portfolioSyncHistory(params.brokerAccountId, params.limit);
      
      const syncHistory: PortfolioSyncHistory[] = (response.data || []).map((sync: any) => ({
        id: sync.id || '',
        firebase_uid: sync.firebase_uid || '',
        broker_account_id: params.brokerAccountId,
        broker_type: sync.broker_type || '',
        sync_type: sync.sync_type || 'full',
        sync_trigger: sync.sync_trigger || 'manual',
        sync_status: sync.sync_status || 'pending',
        sync_started_at: sync.sync_started_at || '',
        sync_completed_at: sync.sync_completed_at,
        sync_duration_ms: sync.sync_duration_ms,
        holdings_synced_count: sync.holdings_synced_count,
        transactions_synced_count: sync.transactions_synced_count,
        error_message: sync.error_message,
        error_code: sync.error_code,
      }));
      
      return syncHistory;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch sync history';
      return rejectWithValue(errorMessage);
    }
  }
);

export const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    setPortfolio: (state, action: { payload: ConsolidatedPortfolio }) => {
      // ✅ Store pre-calculated data from backend (no calculations here)
      state.consolidated = action.payload;
      state.lastUpdated = Date.now();
      state.error = null;
    },
    setLoading: (state, action: { payload: boolean }) => {
      state.loading = action.payload;
    },
    setError: (state, action: { payload: string | null }) => {
      state.error = action.payload;
    },
    clearPortfolio: (state) => {
      state.consolidated = null;
      state.holdings = [];
      state.byBroker = {};
      state.transactions = [];
      state.syncStatus = [];
      state.syncHistory = [];
      state.transactionsTotal = 0;
      state.lastUpdated = null;
      state.error = null;
      state.syncInProgress = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch consolidated portfolio
      .addCase(fetchConsolidatedPortfolio.pending, (state) => {
        // Don't clear existing portfolio data during refresh - just set loading flag
        state.loading = true;
        state.error = null;
        // Keep existing consolidated data to prevent UI from disappearing
      })
      .addCase(fetchConsolidatedPortfolio.fulfilled, (state, action) => {
        // Handle portfolio data update carefully to prevent UI from disappearing
        if (action.payload) {
          // On initial load (state.consolidated is null), always set the payload
          // This ensures existing DB data is displayed when user logs in
          if (!state.consolidated) {
            // Initial load - set portfolio data from DB
            state.consolidated = action.payload;
            state.lastUpdated = Date.now();
          } else {
            // During refresh/sync: intelligently merge broker accounts
            const existingAccounts = state.consolidated.broker_accounts || [];
            const newAccounts = action.payload.broker_accounts || [];
            
            // If new payload has accounts, merge them intelligently
            // This prevents accounts from disappearing when backend returns partial data
            if (newAccounts.length > 0) {
              // Create a map of new accounts by broker_account_id for efficient lookup
              const newAccountsMap = new Map(
                newAccounts.map(acc => [acc.broker_account_id, acc])
              );
              
              // Merge: new accounts take precedence, but preserve existing accounts
              // that are not in the new payload (to prevent disappearing during partial syncs)
              const mergedAccounts: typeof existingAccounts = [];
              
              // First, add all new/updated accounts from the payload
              newAccounts.forEach(newAcc => {
                mergedAccounts.push(newAcc);
              });
              
              // Then, add existing accounts that are not in the new payload
              // This prevents accounts from disappearing during partial backend responses
              existingAccounts.forEach(existingAcc => {
                if (!newAccountsMap.has(existingAcc.broker_account_id)) {
                  // Only preserve existing account if it's not too old (within last 5 minutes)
                  // This prevents stale accounts from persisting indefinitely
                  const accountAge = Date.now() - (state.lastUpdated || 0);
                  const maxAge = 5 * 60 * 1000; // 5 minutes
                  
                  if (accountAge < maxAge) {
                    mergedAccounts.push(existingAcc);
                  }
                }
              });
              
              // Update consolidated portfolio with merged accounts
              state.consolidated = {
                ...action.payload,
                broker_accounts: mergedAccounts,
              };
              state.lastUpdated = Date.now();
            } else if (existingAccounts.length > 0) {
              // New payload is empty but we have existing accounts
              // Preserve existing accounts temporarily (might be a sync in progress)
              // But only if the data is recent (within last 2 minutes)
              const accountAge = Date.now() - (state.lastUpdated || 0);
              const maxAge = 2 * 60 * 1000; // 2 minutes
              
              if (accountAge < maxAge) {
                // Preserve existing accounts
                state.consolidated = {
                  ...action.payload,
                  broker_accounts: existingAccounts,
                };
              } else {
                // Data is stale, accept the empty payload
                state.consolidated = action.payload;
              }
              state.lastUpdated = Date.now();
            } else {
              // Both are empty - just update other fields
              state.consolidated = action.payload;
              state.lastUpdated = Date.now();
            }
          }
        }
        // If payload is null/undefined, preserve existing consolidated data
        // This prevents clearing the UI when refresh fails or returns invalid data
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchConsolidatedPortfolio.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch holdings
      .addCase(fetchHoldings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHoldings.fulfilled, (state, action) => {
        state.holdings = action.payload;
        
        // Group holdings by broker account
        const byBroker: Record<string, DetailedHolding[]> = {};
        action.payload.forEach((holding) => {
          const brokerId = holding.broker_account_id;
          if (!byBroker[brokerId]) {
            byBroker[brokerId] = [];
          }
          byBroker[brokerId].push(holding);
        });
        state.byBroker = byBroker;
        
        state.lastUpdated = Date.now();
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchHoldings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Sync portfolio
      .addCase(syncPortfolio.pending, (state, action) => {
        const brokerAccountId = action.meta.arg.brokerAccountId || 'all';
        state.syncInProgress[brokerAccountId] = true;
      })
      .addCase(syncPortfolio.fulfilled, (state, action) => {
        const brokerAccountId = action.payload.brokerAccountId || 'all';
        state.syncInProgress[brokerAccountId] = false;
        // Refresh portfolio after sync
        // Note: Actual refresh should be triggered by the component
      })
      .addCase(syncPortfolio.rejected, (state, action) => {
        const brokerAccountId = action.meta.arg.brokerAccountId || 'all';
        state.syncInProgress[brokerAccountId] = false;
        state.error = action.payload as string;
        // CRITICAL: Do NOT clear existing portfolio data on sync failure
        // Preserve all existing data in the UI
        // Portfolio data remains unchanged - user can still see existing data
      })
      // Fetch transactions
      .addCase(fetchTransactions.pending, (state) => {
        state.transactionsLoading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.transactions = action.payload.transactions;
        state.transactionsTotal = action.payload.total;
        state.transactionsLoading = false;
        state.error = null;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.transactionsLoading = false;
        state.error = action.payload as string;
      })
      // Fetch sync status
      .addCase(fetchSyncStatus.pending, (state) => {
        state.syncLoading = true;
        state.error = null;
      })
      .addCase(fetchSyncStatus.fulfilled, (state, action) => {
        state.syncStatus = action.payload;
        state.syncLoading = false;
        state.error = null;
      })
      .addCase(fetchSyncStatus.rejected, (state, action) => {
        state.syncLoading = false;
        state.error = action.payload as string;
      })
      // Fetch sync history
      .addCase(fetchSyncHistory.pending, (state) => {
        state.syncLoading = true;
        state.error = null;
      })
      .addCase(fetchSyncHistory.fulfilled, (state, action) => {
        state.syncHistory = action.payload;
        state.syncLoading = false;
        state.error = null;
      })
      .addCase(fetchSyncHistory.rejected, (state, action) => {
        state.syncLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setPortfolio, setLoading, setError, clearPortfolio } = portfolioSlice.actions;
export default portfolioSlice.reducer;

